/**
 * Promotions: "20% off all dresses", "buy 2 get 1 free", "the second one half price".
 *
 * Worked out here and nowhere else. The till runs this to show the customer
 * the price, offline or not; the server runs it again on the promotions the
 * till says it used, with its own prices, and keeps its own answer. So a till
 * can never give away more than a promotion allows, and an offline till and
 * the back office can never disagree about what a promotion was worth.
 *
 * The rules, chosen to be predictable at a counter rather than clever:
 *   - A piece gets at most ONE promotion. They never stack.
 *   - Where promotions overlap, the combination that saves the customer the
 *     most in this sale wins. (Taking the single biggest promotion first is not
 *     the same thing: "2+1" can swallow a sweater that was 25% off on its own
 *     and leave the customer worse off than giving each its own pieces.)
 *   - Buy X get Y: the pieces are lined up dearest first and taken in groups of
 *     X+Y; in each full group the Y cheapest are the ones reduced. A group that
 *     is not complete gets nothing.
 *   - Returns, and items priced at the till, never take part.
 *
 * A promotion that has started cannot be changed, only stopped - see the
 * server. That is what makes "which promotions were running when this sale was
 * made" a question with one answer, even for a sale uploaded days later.
 */
import { type Minor, percentOf } from './money.js';

export type PromoKind = 'percent' | 'bxgy';
export type PromoScope = 'all' | 'categories' | 'brands' | 'products';

export interface Promotion {
  id: string;
  nameEn: string;
  nameAr: string;
  kind: PromoKind;
  /** percent: off every piece. bxgy: off each of the Y pieces (100 = free). Whole numbers, 1-100. */
  percent: number;
  /** bxgy only: buy this many... */
  buyQty: number;
  /** ...and this many more are reduced. */
  getQty: number;
  scope: PromoScope;
  /** The category, brand or product ids the scope names. Empty for 'all'. */
  targetIds: string[];
  startsAt: string;
  /** Null: runs until stopped. */
  endsAt: string | null;
}

/** One line of a sale, as far as promotions are concerned. */
export interface PromoLine {
  id: string;
  productId: string;
  categoryId?: string | null;
  brandId?: string | null;
  qty: number;
  unitPrice: Minor;
  isReturn?: boolean;
  openPrice?: boolean;
}

export interface PromoHit {
  promoId: string;
  /** Taken off this line. Zero for a piece that counted towards a group but was paid in full. */
  amount: Minor;
}

export function promoRunning(p: Pick<Promotion, 'startsAt' | 'endsAt'>, at: string | number | Date): boolean {
  const t = new Date(at).getTime();
  return new Date(p.startsAt).getTime() <= t && (!p.endsAt || t < new Date(p.endsAt).getTime());
}

export function inScope(p: Promotion, l: Pick<PromoLine, 'productId' | 'categoryId' | 'brandId'>): boolean {
  switch (p.scope) {
    case 'all':
      return true;
    case 'categories':
      return !!l.categoryId && p.targetIds.includes(l.categoryId);
    case 'brands':
      return !!l.brandId && p.targetIds.includes(l.brandId);
    case 'products':
      return p.targetIds.includes(l.productId);
    default:
      return false;
  }
}

/** A promotion that could never do anything is refused when it is made; this is the same test. */
export function promoProblem(p: Omit<Promotion, 'id'>): string | null {
  if (!p.nameEn.trim() && !p.nameAr.trim()) return 'name';
  if (!Number.isInteger(p.percent) || p.percent < 1 || p.percent > 100) return 'percent';
  if (p.kind === 'percent' && p.percent === 100) return 'percent';
  if (p.kind === 'bxgy' && (!Number.isInteger(p.buyQty) || p.buyQty < 1 || p.buyQty > 20)) return 'buy';
  if (p.kind === 'bxgy' && (!Number.isInteger(p.getQty) || p.getQty < 1 || p.getQty > 20)) return 'get';
  if (p.scope !== 'all' && p.targetIds.length === 0) return 'targets';
  if (Number.isNaN(new Date(p.startsAt).getTime())) return 'dates';
  if (p.endsAt && new Date(p.endsAt).getTime() <= new Date(p.startsAt).getTime()) return 'dates';
  return null;
}

function evaluate(p: Promotion, lines: PromoLine[]): { hits: Map<string, Minor>; total: Minor } {
  const hits = new Map<string, Minor>();
  const mine = lines.filter((l) => inScope(p, l));
  if (p.kind === 'percent') {
    for (const l of mine) hits.set(l.id, percentOf(l.unitPrice * l.qty, p.percent));
  } else {
    const pieces = mine
      .flatMap((l) => Array.from({ length: l.qty }, () => ({ lineId: l.id, price: l.unitPrice })))
      .sort((a, b) => b.price - a.price || (a.lineId < b.lineId ? -1 : a.lineId > b.lineId ? 1 : 0));
    const size = p.buyQty + p.getQty;
    const groups = Math.floor(pieces.length / size);
    for (let g = 0; g < groups; g++) {
      const chunk = pieces.slice(g * size, (g + 1) * size);
      chunk.forEach((piece, i) => {
        const off = i >= p.buyQty ? percentOf(piece.price, p.percent) : 0;
        hits.set(piece.lineId, (hits.get(piece.lineId) ?? 0) + off);
      });
    }
  }
  let total = 0;
  for (const v of hits.values()) total += v;
  return { hits, total };
}

/**
 * Which promotion each line gets, and how much it takes off. Lines with no
 * promotion are simply absent from the answer.
 *
 * @param promos  the promotions to consider - the caller decides which are
 *                running; this only does the arithmetic.
 */
export function applyPromotions(lines: PromoLine[], promos: Promotion[]): Map<string, PromoHit> {
  const eligible = lines.filter((l) => !l.isReturn && !l.openPrice && l.qty > 0 && l.unitPrice > 0);
  // Sorted by id so that equal answers are always settled the same way, on the till and on the server.
  const relevant = promos
    .filter((p) => eligible.some((l) => inScope(p, l)))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (!relevant.length) return new Map();

  let best: ReturnType<typeof inOrder> | null = null;
  const consider = (order: Promotion[]) => {
    const r = inOrder(eligible, order);
    // Equal savings: the one whose promotions, in the order they took their
    // pieces, read first. The till and the server both reach it, because a
    // promotion that took nothing never changes the answer.
    if (!best || r.total > best.total || (r.total === best.total && r.key < best.key)) best = r;
  };
  if (relevant.length <= 6) {
    for (const order of permutations(relevant)) consider(order);
  } else {
    // More than six offers on one sale is not a real shop; do the simple thing.
    consider(biggestFirst(eligible, relevant));
  }
  return best!.out;
}

/** Each promotion in turn takes what it covers from what is left. */
function inOrder(lines: PromoLine[], order: Promotion[]): { out: Map<string, PromoHit>; total: Minor; key: string } {
  const out = new Map<string, PromoHit>();
  const took: string[] = [];
  let left = lines;
  let total = 0;
  for (const p of order) {
    const r = evaluate(p, left);
    if (r.total <= 0) continue;
    for (const [lineId, amount] of r.hits) out.set(lineId, { promoId: p.id, amount });
    left = left.filter((l) => !r.hits.has(l.id));
    total += r.total;
    took.push(p.id);
  }
  return { out, total, key: took.join('|') };
}

function biggestFirst(lines: PromoLine[], promos: Promotion[]): Promotion[] {
  const order: Promotion[] = [];
  let left = lines;
  const rest = [...promos];
  for (;;) {
    let pick = -1;
    let most = 0;
    let hits = new Map<string, Minor>();
    rest.forEach((p, i) => {
      const r = evaluate(p, left);
      if (r.total > most) [pick, most, hits] = [i, r.total, r.hits];
    });
    if (pick < 0) return order;
    order.push(rest.splice(pick, 1)[0]);
    left = left.filter((l) => !hits.has(l.id));
  }
}

function* permutations<T>(xs: T[]): Generator<T[]> {
  if (xs.length <= 1) {
    yield xs;
    return;
  }
  for (let i = 0; i < xs.length; i++) {
    const rest = [...xs.slice(0, i), ...xs.slice(i + 1)];
    for (const p of permutations(rest)) yield [xs[i], ...p];
  }
}

/** "20% off", "Buy 2 get 1 free", "Buy 1, the next at 50% off" - in the words a shop uses. */
export function promoSummary(p: Pick<Promotion, 'kind' | 'percent' | 'buyQty' | 'getQty'>, lang: 'en' | 'ar'): string {
  if (p.kind === 'percent') return lang === 'ar' ? `خصم ${p.percent}%` : `${p.percent}% off`;
  if (p.percent === 100) {
    return lang === 'ar' ? `اشترِ ${p.buyQty} وخذ ${p.getQty} مجاناً` : `Buy ${p.buyQty} get ${p.getQty} free`;
  }
  return lang === 'ar'
    ? `اشترِ ${p.buyQty} و${p.getQty} بخصم ${p.percent}%`
    : `Buy ${p.buyQty}, ${p.getQty} more at ${p.percent}% off`;
}

/** The short badge on a till tile: "-20%", "2+1", "2+1 -50%". */
export function promoBadge(p: Pick<Promotion, 'kind' | 'percent' | 'buyQty' | 'getQty'>): string {
  if (p.kind === 'percent') return `-${p.percent}%`;
  return p.percent === 100 ? `${p.buyQty}+${p.getQty}` : `${p.buyQty}+${p.getQty} -${p.percent}%`;
}
